/**
 * App icon badge utilities for PWA Home Screen icons.
 *
 * Uses the Badging API (`navigator.setAppBadge` / `navigator.clearAppBadge`).
 * Platform support:
 *   - iOS Safari >= 16.4 (Home Screen PWA only, with notification permission granted)
 *   - macOS Safari >= 14 (with PWA installation)
 *   - Desktop Chrome / Edge (PWA installed)
 *   - NOT supported: Android Chrome, iOS Safari tab, non-PWA contexts
 *
 * Where the platform does not support badging, calls are silently skipped.
 */

/**
 * Check if the Badging API is available on the current platform.
 * @returns {boolean}
 */
export function isBadgingSupported() {
  return 'setAppBadge' in navigator
}

/**
 * Set the app icon badge count.
 * Silently no-ops on platforms that don't support the Badging API.
 *
 * @param {number} count - Unread notification count. 0 clears the badge.
 * @returns {Promise<void>}
 */
export async function setAppBadge(count) {
  if (!isBadgingSupported()) {
    return
  }

  try {
    if (count > 0) {
      await navigator.setAppBadge(count)
    } else {
      await navigator.clearAppBadge()
    }
  } catch {
    // Best-effort: browser may reject if no PWA installation or permission
  }
}

/**
 * Clear the app icon badge entirely.
 * Equivalent to `setAppBadge(0)`.
 *
 * @returns {Promise<void>}
 */
export async function clearAppBadge() {
  return setAppBadge(0)
}
